import { devError } from '../utils/devLogger';
/**
 * Enhanced Role Management Service
 *
 * Provides comprehensive role-based access control with granular permissions
 * for Admin, Facilitator, Assessor, and Learner roles.
 *
 * Features:
 * - Granular permission system
 * - Cohort-based access control
 * - Time-based restrictions (for Assessor role)
 * - Audit trail integration
 * - Enhanced security features
 */

import { auth, db } from '../firebase';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import auditTrailService from './auditTrailService';

export const ROLE_ALIASES = {
  // External role aliases (long-standing)
  external_facilitator: 'external_facilitator_limited',
  external_viewer: 'external_viewer_reporting',
  // Legacy Launchpad / Bootcamp app roles — treated as learners in the LMS
  applicant:  'learner',
  student:    'learner',
  graduate:   'learner',
  candidate:  'learner',
};

export const normalizeRole = (role) => ROLE_ALIASES[role] || role || null;
export const isAdminRole = (role) => normalizeRole(role) === 'admin';
export const isFacilitatorRole = (role) => normalizeRole(role) === 'facilitator';
export const isAssessorRole = (role) => normalizeRole(role) === 'assessor';
export const isExternalFacilitatorRole = (role) => normalizeRole(role) === 'external_facilitator_limited';
export const isExternalViewerRole = (role) => normalizeRole(role) === 'external_viewer_reporting';
export const isExternalRole = (role) => isExternalFacilitatorRole(role) || isExternalViewerRole(role);
export const canManageSchedules = (role) => isAdminRole(role) || isFacilitatorRole(role);
export const canBroadcastToLearners = (role) => (
  isAdminRole(role) ||
  isFacilitatorRole(role) ||
  normalizeRole(role) === 'qa'
);
export const canUseRestrictedMessaging = (role) => canBroadcastToLearners(role) || isExternalFacilitatorRole(role);
export const canAccessAssignmentWorkspace = (role) => (
  isAdminRole(role) ||
  isFacilitatorRole(role) ||
  isAssessorRole(role) ||
  isExternalFacilitatorRole(role)
);
export const canViewResources = (role) => (
  isAdminRole(role) ||
  isFacilitatorRole(role) ||
  normalizeRole(role) === 'qa' ||
  normalizeRole(role) === 'learner' ||
  isExternalFacilitatorRole(role)
);
export const canManageResourceStructure = (role) => (
  isAdminRole(role) ||
  isFacilitatorRole(role) ||
  normalizeRole(role) === 'qa'
);
export const getDefaultRouteForRole = (role) => {
  switch (normalizeRole(role)) {
    case 'admin':
      return '/admin';
    case 'facilitator':
      return '/facilitator';
    case 'assessor':
      return '/assessor';
    case 'qa':
      return '/qa';
    case 'external_facilitator_limited':
      return '/external-facilitator';
    case 'external_viewer_reporting':
      return '/external-viewer';
    case 'learner':
      return '/dashboard';
    default:
      return '/login';
  }
};

// Role definitions with detailed permissions
export const ROLES = {
  ADMIN: {
    name: 'admin',
    displayName: 'Administrator',
    description: 'Full system access with all administrative privileges',
    level: 4,
    permissions: {
      // User Management
      createUsers: true,
      deleteUsers: true,
      modifyUserRoles: true,
      viewAllUsers: true,
      managePasswords: true,
      bulkOperations: true,

      // Data Access
      viewAllCohorts: true,
      manageCohorts: true,
      viewAllPrograms: true,
      managePrograms: true,
      accessSystemSettings: true,
      exportData: true,

      // Document Management
      uploadDocumentsForOthers: true,
      deleteDocuments: true,
      overrideDocumentStatus: true,
      viewAllDocuments: true,

      // Assessment & Grading
      createAssessments: true,
      gradeAssessments: true,
      viewAllGrades: true,
      modifyGrades: true,

      // Reports & Analytics
      viewSystemAnalytics: true,
      generateReports: true,
      accessAuditTrails: true,
      manageCompliance: true,

      // Integration Management
      manageIntegrations: true,
      systemConfiguration: true,
      backupRestore: true
    }
  },

  FACILITATOR: {
    name: 'facilitator',
    displayName: 'Facilitator',
    description: 'Limited to assigned cohorts with read/write access for students',
    level: 3,
    permissions: {
      // User Management (Limited)
      viewAssignedLearners: true,
      modifyLearnerData: true,
      uploadDocumentsForLearners: true,

      // Cohort Management (Limited)
      viewAssignedCohorts: true,
      manageAssignedContent: true,
      createModules: true,
      editModules: true,

      // Assessment & Grading (Limited)
      createAssessments: true,
      gradeAssignedLearners: true,
      viewAssignedGrades: true,

      // Attendance & Progress
      markAttendance: true,
      generateProgressReports: true,
      viewLearnerProgress: true,

      // Communication
      messageAssignedLearners: true,
      sendNotifications: true,

      // Limited Data Access
      exportAssignedData: true,
      viewCohortAnalytics: true
    },
    restrictions: {
      cohortBased: true,
      timeRestricted: false,
      requiresVerification: false
    }
  },

  ASSESSOR: {
    name: 'assessor',
    displayName: 'Assessor',
    description: 'Subset of Facilitator: assignments, POE docs, progress reports post-start date',
    level: 2,
    permissions: {
      // Assessment Focus Only
      gradeAssignments: true,
      reviewPOEDocuments: true,
      generateProgressReports: true,
      viewLearnerSubmissions: true,

      // Limited Document Access
      viewDocuments: true,
      downloadDocuments: true,

      // Basic Communication
      provideFeedback: true,
      messageRegardingAssessments: true
    },
    restrictions: {
      cohortBased: true,
      timeRestricted: true, // Only post-program-start
      postStartDateOnly: true,
      requiresVerification: true,
      limitedScope: ['assignments', 'assessments', 'progress-reports']
    }
  },

  LEARNER: {
    name: 'learner',
    displayName: 'Learner',
    description: 'Basic learner access to own content and submissions',
    level: 1,
    permissions: {
      // Self-Service Only
      viewOwnProfile: true,
      editOwnProfile: true,
      uploadOwnDocuments: true,
      viewOwnGrades: true,
      submitAssignments: true,
      takeAssessments: true,
      viewOwnProgress: true,

      // Communication
      messageInstructors: true,
      receiveNotifications: true,

      // Content Access
      viewAssignedModules: true,
      downloadResources: true
    },
    restrictions: {
      selfOnly: true,
      requiresVerification: false
    }
  },

  QA: {
    name: 'qa',
    displayName: 'Quality Assurance',
    description: 'System-wide document review and quality control',
    level: 3,
    permissions: {
      // Document Review (Primary Function)
      viewAllDocuments: true,
      reviewDocuments: true,
      approveDocuments: true,
      rejectDocuments: true,
      flagDocuments: true,
      addReviewNotes: true,
      viewDocumentHistory: true,
      downloadDocuments: true,

      // Reports & Analytics
      generateQAReports: true,
      viewValidationStats: true,
      viewReviewMetrics: true,
      exportQAData: true,

      // Audit & Compliance
      viewDocumentAuditTrail: true,
      trackTurnaroundTime: true,
      viewRejectionReasons: true,

      // Limited User View (Read-only)
      viewAllUsers: true,
      viewUserDocuments: true,

      // Communication
      provideFeedback: true,
      notifyDocumentOwners: true
    },
    restrictions: {
      cohortBased: false,
      timeRestricted: false,
      requiresVerification: false,
      documentReviewOnly: true
    }
  },

  EXTERNAL_FACILITATOR_LIMITED: {
    name: 'external_facilitator_limited',
    displayName: 'External Facilitator (Limited)',
    description: 'External provider with assigned-programme learner access, attendance confirmation, controlled messaging, and optional assignment marking',
    level: 2,
    permissions: {
      // Learner Management (View Only)
      viewAssignedLearners: true,
      viewLearnerProgress: true,

      // Attendance (Can Confirm/Override)
      viewAttendance: true,
      markAttendance: true,
      confirmAttendance: true,

      // Assessment & Grading (Optional)
      viewAssignedSubmissions: true,
      viewAssignedGrades: true,
      gradeAssignments: true,
      gradeAssessments: false,

      // Communication (Limited - 1:1 Only)
      messageAssignedLearners: true,
      sendDirectMessages: true,
      sendNotifications: false,
      broadcastMessages: false,

      // Scheduling (View Only)
      viewSchedule: true,
      createSchedule: false,
      modifySchedule: false,

      // Resources (View Only)
      viewResources: true,
      downloadResources: true,
      uploadResources: false,
      manageResources: false,

      // Reports & Analytics (View and Export)
      generateProgressReports: true,
      viewCohortAnalytics: true,
      exportAssignedData: true,
      viewReports: true,
      downloadReports: true
    },
    restrictions: {
      cohortBased: true,
      programmeScoped: true,
      timeRestricted: false,
      requiresVerification: false,
      noSystemAdmin: true,
      externalProvider: true,
      readOnlyGrading: true,
      limitedMessaging: true,
      noScheduling: true,
      noResourceManagement: true
    }
  },

  EXTERNAL_VIEWER_REPORTING: {
    name: 'external_viewer_reporting',
    displayName: 'External Viewer (Reporting)',
    description: 'Read-only access for board reporting - no editing or messaging',
    level: 1,
    permissions: {
      // Learner Information (Read Only)
      viewAssignedLearners: true,
      viewLearnerProgress: true,

      // Attendance (Read Only)
      viewAttendance: true,

      // Assessment & Grading (View Only)
      viewAssignedGrades: true,

      // Communication (None)
      messageAssignedLearners: false,
      sendDirectMessages: false,
      sendNotifications: false,
      broadcastMessages: false,

      // Reports & Analytics (Primary Function)
      generateReports: true,
      generateProgressReports: true,
      viewCohortAnalytics: true,
      exportAssignedData: true,
      viewReports: true,
      downloadReports: true,

      // Resources (None)
      viewResources: false,
      downloadResources: false
    },
    restrictions: {
      cohortBased: true,
      programmeScoped: true,
      timeRestricted: false,
      requiresVerification: false,
      noSystemAdmin: true,
      externalProvider: true,
      readOnlyAccess: true,
      noMessaging: true,
      noEditing: true
    }
  }
};

class RoleManagementService {
  constructor() {
    this.currentUser = null;
    this.userProfile = null;
  }

  /**
   * Initialize service with current user context
   */
  async initialize() {
    this.currentUser = auth.currentUser;
    if (this.currentUser) {
      const userDoc = await getDoc(doc(db, 'users', this.currentUser.uid));
      const profile = userDoc.data();
      this.userProfile = profile
        ? {
            ...profile,
            role: normalizeRole(profile.role)
          }
        : null;
    }
  }

  /**
   * Get user role with full details
   */
  getUserRole(userRole = null) {
    const role = normalizeRole(userRole || this.userProfile?.role);
    return ROLES[role?.toUpperCase()] || null;
  }

  /**
   * Check if user has specific permission
   */
  async hasPermission(permission, targetUserId = null, additionalContext = {}) {
    await this.initialize();

    if (!this.userProfile) {
      return false;
    }

    const role = this.getUserRole();
    if (!role) {
      return false;
    }

    // Check basic permission
    if (!role.permissions[permission]) {
      return false;
    }

    // Apply role-specific restrictions
    if (role.restrictions) {
      // Cohort-based restrictions for Facilitator and Assessor
      if (role.restrictions.cohortBased && targetUserId) {
        const hasAccess = await this.canAccessUser(targetUserId);
        if (!hasAccess) {
          return false;
        }
      }

      // Time-based restrictions for Assessor
      if (role.restrictions.timeRestricted) {
        const programStarted = await this.isProgramStarted(additionalContext.programId);
        if (!programStarted) {
          return false;
        }
      }

      // Scope restrictions for Assessor
      if (role.restrictions.limitedScope && additionalContext.scope) {
        if (!role.restrictions.limitedScope.includes(additionalContext.scope)) {
          return false;
        }
      }
    }

    return true;
  }

  /**
   * Check if facilitator/assessor/external can access specific user
   */
  async canAccessUser(targetUserId) {
    const currentRole = normalizeRole(this.userProfile.role);
    if (currentRole === 'admin') {
      return true; // Admins can access all users
    }

    if (['facilitator', 'assessor', 'external_facilitator_limited', 'external_viewer_reporting'].includes(currentRole)) {
      // Check if user is in facilitator's assigned cohorts
      const assignedCohorts = this.userProfile.assignedCohorts || [];
      const assignedPrograms = this.userProfile.assignedPrograms || [];
      const targetUserDoc = await getDoc(doc(db, 'users', targetUserId));
      const targetUserData = targetUserDoc.data() || {};
      const targetUserCohort = targetUserData.cohortId;
      const targetUserProgram = targetUserData.programId;

      return assignedCohorts.includes(targetUserCohort) || assignedPrograms.includes(targetUserProgram);
    }

    return false;
  }

  /**
   * Check if program has started (for Assessor time restrictions)
   */
  async isProgramStarted(programId) {
    if (!programId) {
      return true; // Default to allowing access if no program specified
    }

    try {
      const programDoc = await getDoc(doc(db, 'programs', programId));
      const programData = programDoc.data();

      if (!programData?.startDate) {
        return true; // Allow access if no start date specified
      }

      const startDate = programData.startDate.toDate ? programData.startDate.toDate() : new Date(programData.startDate);
      return new Date() >= startDate;
    } catch (error) {
      devError('Error checking program start date:', error);
      return false; // Fail-closed — deny access when the date cannot be verified
    }
  }

  /**
   * Get filtered users based on current user's role and permissions
   */
  async getAccessibleUsers() {
    await this.initialize();

    if (!this.userProfile) {
      return [];
    }

    const currentRole = normalizeRole(this.userProfile.role);
    if (currentRole === 'admin') {
      // Admins can see all users
      const usersQuery = query(collection(db, 'users'));
      const snapshot = await getDocs(usersQuery);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    }

    if (['facilitator', 'assessor', 'external_facilitator_limited', 'external_viewer_reporting'].includes(currentRole)) {
      const assignedCohorts = this.userProfile.assignedCohorts || [];
      const assignedPrograms = this.userProfile.assignedPrograms || [];
      if (assignedCohorts.length === 0 && assignedPrograms.length === 0) {
        return [];
      }
      const users = new Map();

      if (assignedCohorts.length > 0) {
        const usersQuery = query(
          collection(db, 'users'),
          where('cohortId', 'in', assignedCohorts.slice(0, 10))
        );
        const snapshot = await getDocs(usersQuery);
        snapshot.docs.forEach((docSnap) => {
          users.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
        });
      }

      if (assignedPrograms.length > 0) {
        const usersQuery = query(
          collection(db, 'users'),
          where('programId', 'in', assignedPrograms.slice(0, 10))
        );
        const snapshot = await getDocs(usersQuery);
        snapshot.docs.forEach((docSnap) => {
          users.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
        });
      }

      return Array.from(users.values());
    }

    // Learners can only see themselves
    return [this.userProfile];
  }

  /**
   * Assign cohorts to facilitator/assessor
   */
  async assignCohortsToUser(userId, cohortIds, assignedBy = null) {
    await this.initialize();

    // Only admins can assign cohorts
    if (normalizeRole(this.userProfile.role) !== 'admin') {
      throw new Error('Only administrators can assign cohorts');
    }

    try {
      await updateDoc(doc(db, 'users', userId), {
        assignedCohorts: cohortIds,
        cohortsAssignedBy: assignedBy || this.currentUser.uid,
        cohortsAssignedAt: new Date().toISOString()
      });

      // Log the assignment
      await auditTrailService.logAction('user.cohorts.assigned', {
        targetUserId: userId,
        assignedCohorts: cohortIds,
        assignedBy: assignedBy || this.currentUser.uid,
        assignmentTimestamp: new Date().toISOString()
      });

      return { success: true };
    } catch (error) {
      devError('Error assigning cohorts:', error);
      throw new Error('Failed to assign cohorts: ' + error.message);
    }
  }

  /**
   * Change user role with proper validation and audit
   */
  async changeUserRole(userId, newRole, reason = '') {
    await this.initialize();

    // Only admins can change roles
    if (normalizeRole(this.userProfile.role) !== 'admin') {
      throw new Error('Only administrators can change user roles');
    }

    // Validate new role
    const normalizedNewRole = normalizeRole(newRole);
    if (!ROLES[normalizedNewRole?.toUpperCase()]) {
      throw new Error('Invalid role specified');
    }

    try {
      // Get current user data
      const userDoc = await getDoc(doc(db, 'users', userId));
      const currentUserData = userDoc.data();
      const oldRole = normalizeRole(currentUserData.role);

      // Update role
      await updateDoc(doc(db, 'users', userId), {
        role: normalizedNewRole,
        roleChangedBy: this.currentUser.uid,
        roleChangedAt: new Date().toISOString(),
        roleChangeReason: reason,
        previousRole: oldRole
      });

      // Log the role change
      await auditTrailService.logRoleChanged(userId, oldRole, normalizedNewRole);

      return { success: true };
    } catch (error) {
      devError('Error changing user role:', error);
      throw new Error('Failed to change user role: ' + error.message);
    }
  }

  /**
   * Get role options based on current user's permissions
   */
  getRoleOptions() {
    if (!this.userProfile) {
      return [];
    }

    if (normalizeRole(this.userProfile.role) === 'admin') {
      // Admins can assign any role
      return Object.values(ROLES).map(role => ({
        value: role.name,
        label: role.displayName,
        description: role.description
      }));
    }

    // Other roles cannot assign roles
    return [];
  }

  /**
   * Validate role transition
   */
  validateRoleTransition(fromRole, toRole) {
    const normalizedFromRole = normalizeRole(fromRole);
    const normalizedToRole = normalizeRole(toRole);
    const transitions = {
      learner: ['facilitator', 'assessor'], // Learners can be promoted
      assessor: ['facilitator'], // Assessors can be promoted to facilitator
      facilitator: ['assessor'], // Facilitators can be demoted to assessor
      admin: ['admin'] // Admins can only remain admin (prevent accidental demotion)
    };

    return transitions[normalizedFromRole]?.includes(normalizedToRole) || false;
  }

  /**
   * Get user's effective permissions (considering cohort assignments, etc.)
   */
  async getEffectivePermissions(targetUserId = null) {
    await this.initialize();

    const role = this.getUserRole();
    if (!role) {
      return {};
    }

    const permissions = { ...role.permissions };

    // Apply restrictions based on context
    if (role.restrictions?.cohortBased && targetUserId) {
      const canAccess = await this.canAccessUser(targetUserId);
      if (!canAccess) {
        // Remove permissions that require user access
        delete permissions.viewAssignedLearners;
        delete permissions.modifyLearnerData;
        delete permissions.uploadDocumentsForLearners;
        delete permissions.gradeAssignedLearners;
      }
    }

    return permissions;
  }

  /**
   * Security feature: Enhanced password management
   */
  async requirePasswordReset(userId, reason = '') {
    await this.initialize();

    if (normalizeRole(this.userProfile.role) !== 'admin') {
      throw new Error('Only administrators can require password resets');
    }

    try {
      await updateDoc(doc(db, 'users', userId), {
        requiresPasswordReset: true,
        passwordResetReason: reason,
        passwordResetRequiredBy: this.currentUser.uid,
        passwordResetRequiredAt: new Date().toISOString()
      });

      // Log the password reset requirement
      await auditTrailService.logAction('user.password.reset_required', {
        targetUserId: userId,
        reason: reason,
        requiredBy: this.currentUser.uid
      });

      return { success: true };
    } catch (error) {
      devError('Error requiring password reset:', error);
      throw new Error('Failed to require password reset: ' + error.message);
    }
  }

  /**
   * Enhanced verification workflow
   */
  async updateUserVerification(userId, verified, notes = '') {
    await this.initialize();

    if (normalizeRole(this.userProfile.role) !== 'admin') {
      throw new Error('Only administrators can update verification status');
    }

    try {
      await updateDoc(doc(db, 'users', userId), {
        isVerified: verified,
        verificationUpdatedBy: this.currentUser.uid,
        verificationUpdatedAt: new Date().toISOString(),
        verificationNotes: notes
      });

      // Log the verification update
      await auditTrailService.logAction('user.verification.updated', {
        targetUserId: userId,
        verified: verified,
        notes: notes,
        updatedBy: this.currentUser.uid
      });

      return { success: true };
    } catch (error) {
      devError('Error updating verification:', error);
      throw new Error('Failed to update verification: ' + error.message);
    }
  }
}

// Create and export singleton instance
const roleManagementService = new RoleManagementService();
export default roleManagementService;
